import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Settings, Save, Check, Eye, EyeOff, Copy, Shield, CreditCard, AlertTriangle, Rocket, Webhook } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PlatformSetting {
  id: string;
  key: string;
  value: string;
  label: string;
  description: string | null;
  updated_at: string;
}

const SENSITIVE_KEYS = ["stripe_secret_key", "stripe_webhook_secret"];
const READONLY_KEYS = ["stripe_webhook_url"];
const SELECT_KEYS: Record<string, { options: { value: string; label: string }[] }> = {
  stripe_mode: {
    options: [
      { value: "sandbox", label: "🧪 Sandbox (Teste)" },
      { value: "production", label: "🟢 Production (Produção)" },
    ],
  },
};

const STRIPE_KEYS = ["stripe_mode", "stripe_secret_key", "stripe_webhook_secret", "stripe_webhook_url"];

function isStripeKey(key: string) {
  return STRIPE_KEYS.includes(key);
}

function maskValue(value: string) {
  if (!value || value.length < 8) return "••••••••";
  return value.slice(0, 7) + "••••" + value.slice(-4);
}

export default function ManagerSettings() {
  const queryClient = useQueryClient();
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const [cutoverOpen, setCutoverOpen] = useState(false);
  const [cutoverConfirm, setCutoverConfirm] = useState("");
  const [cutoverInfo, setCutoverInfo] = useState<any>(null);
  const [cutoverLoading, setCutoverLoading] = useState(false);
  const [cutoverRunning, setCutoverRunning] = useState(false);
  const [registeringWebhook, setRegisteringWebhook] = useState(false);

  const registerWebhook = async () => {
    setRegisteringWebhook(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "stripe_register_webhook" },
      });
      if (error) throw error;
      toast.success(
        data?.replaced
          ? `Webhook re-registrado (${data.events} eventos). Secret atualizado automaticamente.`
          : `Webhook registrado (${data.events} eventos). Secret salvo automaticamente.`
      );
      queryClient.invalidateQueries({ queryKey: ["admin_settings"] });
    } catch (e: any) {
      toast.error(e.message || "Erro ao registrar webhook");
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const openCutover = async () => {
    setCutoverOpen(true);
    setCutoverConfirm("");
    setCutoverInfo(null);
    setCutoverLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "stripe_validate_key" },
      });
      if (error) throw error;
      setCutoverInfo(data);
    } catch (e: any) {
      toast.error(e.message || "Erro ao validar chave Stripe");
      setCutoverOpen(false);
    } finally {
      setCutoverLoading(false);
    }
  };

  const runCutover = async () => {
    setCutoverRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "stripe_cutover", confirm: "CONFIRMAR" },
      });
      if (error) throw error;
      toast.success(`Cutover concluído: ${data?.wiped?.subscriptions || 0} assinaturas e ${data?.wiped?.customers || 0} clientes resetados`);
      setCutoverOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin_settings"] });
    } catch (e: any) {
      toast.error(e.message || "Erro no cutover");
    } finally {
      setCutoverRunning(false);
    }
  };


  const { data: settings, isLoading } = useQuery({
    queryKey: ["admin_settings"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "settings" },
      });
      if (error) throw error;
      return (data?.settings || []) as PlatformSetting[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "update_setting", key, value },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin_settings"] });
      setEditedValues((prev) => {
        const next = { ...prev };
        delete next[variables.key];
        return next;
      });
      setSavingKey(null);
      toast.success("Configuração salva com sucesso!");
    },
    onError: () => {
      setSavingKey(null);
      toast.error("Erro ao salvar configuração");
    },
  });

  const handleSave = (key: string) => {
    const value = editedValues[key];
    if (value === undefined) return;
    setSavingKey(key);
    updateMutation.mutate({ key, value });
  };

  const handleChange = (key: string, value: string, originalValue: string) => {
    if (value === originalValue) {
      setEditedValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setEditedValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const toggleReveal = (key: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success("Copiado!");
  };

  const stripeSettings = (settings || []).filter((s) => isStripeKey(s.key));
  const otherSettings = (settings || []).filter((s) => !isStripeKey(s.key));

  const renderSettingCard = (setting: PlatformSetting, i: number) => {
    const isSensitive = SENSITIVE_KEYS.includes(setting.key);
    const isReadonly = READONLY_KEYS.includes(setting.key);
    const isSelect = SELECT_KEYS[setting.key];
    const isEdited = editedValues[setting.key] !== undefined;
    const isSaving = savingKey === setting.key;
    const displayValue = editedValues[setting.key] ?? setting.value;
    const isRevealed = revealedKeys.has(setting.key);

    return (
      <motion.div
        key={setting.key}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.06 }}
      >
        <Card className="glass-card border-warmz-glass-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              {setting.label}
              {isSensitive && (
                <Badge variant="outline" className="text-[10px] border-yellow-500/40 text-yellow-400">
                  <Shield className="h-3 w-3 mr-1" />
                  Sensível
                </Badge>
              )}
              {isReadonly && (
                <Badge variant="outline" className="text-[10px] border-muted-foreground/40 text-muted-foreground">
                  Somente leitura
                </Badge>
              )}
            </CardTitle>
            {setting.description && (
              <p className="text-xs text-muted-foreground">{setting.description}</p>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-2">
              {isSelect ? (
                <Select
                  value={displayValue}
                  onValueChange={(val) => handleChange(setting.key, val, setting.value)}
                >
                  <SelectTrigger className="bg-secondary/40 border-warmz-glass-border/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSelect.options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : isReadonly ? (
                <Input
                  value={displayValue}
                  readOnly
                  className="bg-secondary/40 border-warmz-glass-border/30 text-muted-foreground font-mono text-xs"
                />
              ) : (
                <Input
                  type={isSensitive && !isRevealed ? "password" : "text"}
                  value={isSensitive && !isRevealed && !isEdited ? maskValue(setting.value) : displayValue}
                  onChange={(e) =>
                    handleChange(setting.key, e.target.value, setting.value)
                  }
                  onFocus={() => {
                    if (isSensitive && !isRevealed && !isEdited) {
                      setEditedValues((prev) => ({ ...prev, [setting.key]: setting.value }));
                    }
                  }}
                  placeholder={isSensitive ? "Cole a chave aqui..." : ""}
                  className="bg-secondary/40 border-warmz-glass-border/30 font-mono text-xs"
                />
              )}

              {isSensitive && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => toggleReveal(setting.key)}
                  className="shrink-0"
                  title={isRevealed ? "Ocultar" : "Revelar"}
                >
                  {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              )}

              {isReadonly ? (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => copyToClipboard(displayValue)}
                  className="shrink-0"
                  title="Copiar"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  size="icon"
                  variant={isEdited ? "default" : "ghost"}
                  disabled={!isEdited || isSaving}
                  onClick={() => handleSave(setting.key)}
                  className="shrink-0"
                >
                  {isSaving ? (
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isEdited ? (
                    <Save className="h-4 w-4" />
                  ) : (
                    <Check className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Atualizado em{" "}
              {new Date(setting.updated_at).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
          <Settings className="h-7 w-7 text-primary" />
          Configurações
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie os valores e parâmetros da plataforma
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {/* Stripe Section */}
          {stripeSettings.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Stripe
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={registerWebhook}
                    disabled={registeringWebhook}
                    className="border-primary/40 text-primary hover:bg-primary/10"
                  >
                    <Webhook className="h-4 w-4 mr-1.5" />
                    {registeringWebhook ? "Registrando..." : "Registrar webhook automaticamente"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openCutover}
                    className="border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
                  >
                    <Rocket className="h-4 w-4 mr-1.5" />
                    Go-Live (Sandbox → Production)
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stripeSettings.map((s, i) => renderSettingCard(s, i))}
              </div>
            </div>
          )}


          {/* Other Settings */}
          {otherSettings.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
                <Settings className="h-5 w-5 text-accent" />
                Preços & Plataforma
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {otherSettings.map((s, i) => renderSettingCard(s, i + stripeSettings.length))}
              </div>
            </div>
          )}
        </>
      )}

      <AlertDialog open={cutoverOpen} onOpenChange={setCutoverOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              Cutover Stripe (Sandbox → Production)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>Esta ação prepara a plataforma para receber pagamentos reais. Ela é IRREVERSÍVEL.</p>
                {cutoverLoading ? (
                  <p className="text-muted-foreground">Validando chave Stripe...</p>
                ) : cutoverInfo ? (
                  <div className="space-y-2 p-3 rounded-lg bg-secondary/40 border border-warmz-glass-border/30">
                    <div className="flex justify-between"><span className="text-muted-foreground">Chave detectada</span><span className="font-mono">{cutoverInfo.keyPrefix === "live" ? "🟢 sk_live_" : cutoverInfo.keyPrefix === "test" ? "🧪 sk_test_" : "❓ desconhecida"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Stripe Account</span><span className="font-mono text-xs">{cutoverInfo.accountId} ({cutoverInfo.accountCountry})</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Modo atual no DB</span><span className="font-mono">{cutoverInfo.currentMode}</span></div>
                    <div className="border-t border-warmz-glass-border/30 my-2" />
                    <div className="flex justify-between"><span className="text-muted-foreground">Customers a resetar</span><span className="font-bold text-destructive">{cutoverInfo.affected.customers}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Subscriptions a deletar</span><span className="font-bold text-destructive">{cutoverInfo.affected.subscriptions}</span></div>
                  </div>
                ) : null}
                {cutoverInfo?.keyPrefix === "test" && (
                  <p className="text-xs text-yellow-400 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />A chave configurada ainda é de TESTE (sk_test_). Atualize para sk_live_ antes de rodar o cutover em produção.</p>
                )}
                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                  <li>Todas as <strong>subscriptions</strong> da base serão deletadas</li>
                  <li>Todos os <strong>stripe_customer_id</strong> serão limpos</li>
                  <li>Status de assinatura volta para <strong>trialing</strong></li>
                  <li>Logs de webhooks são limpos</li>
                  <li>Usuários precisarão refazer a assinatura no novo ambiente</li>
                </ul>
                <div>
                  <label className="text-xs text-muted-foreground">Digite <strong>CONFIRMAR</strong> para liberar:</label>
                  <Input value={cutoverConfirm} onChange={(e) => setCutoverConfirm(e.target.value)} placeholder="CONFIRMAR" className="mt-1 font-mono" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cutoverRunning}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={cutoverConfirm !== "CONFIRMAR" || cutoverRunning || cutoverLoading}
              onClick={(e) => { e.preventDefault(); runCutover(); }}
              className="bg-destructive hover:bg-destructive/90"
            >
              {cutoverRunning ? "Executando..." : "Executar cutover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
